import { BACKEND_URL } from "@/lib/backendUrl";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarClock, CheckCircle2, FileText, Gavel, Loader2, PenLine, Plus, ShieldCheck,
  Vote } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle } from "@/components/ui/dialog";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { AnnualReport } from "@/components/governance/AnnualReport";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLE = {
  draft: "border-border bg-muted text-muted-foreground",
  notice_issued: "border-primary/30 bg-primary/10 text-primary",
  held: "border-[hsl(var(--teal))]/30 bg-[hsl(var(--teal))]/10 text-[hsl(var(--teal))]",
  minuted: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  archived: "border-border bg-muted text-muted-foreground",
  open_for_discussion: "border-primary/30 bg-primary/10 text-primary",
  open_for_voting: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  awaiting_chair_consent: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
  outcome_published: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
};
const OUTCOME_STYLE = {
  approved: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  rejected: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
  no_quorum: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  tied: "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300",
};
const NEXT_STEP = { draft: "notice_issued", notice_issued: "held", held: "minuted",
  minuted: "archived" };
const STEP_LABEL = { notice_issued: "Issue the notice", held: "Record who attended",
  minuted: "Sign the minutes", archived: "Archive" };

const words = (s) => String(s || "").replace(/_/g, " ");
const when = (iso) => (iso ? `${iso.slice(0, 10)} ${iso.slice(11, 16)}` : "—");

const F = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11.5px] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10.5px] leading-relaxed text-muted-foreground">{hint}</p>}
  </div>
);

const Bar = ({ cast, eligible, quorum }) => {
  const pct = eligible ? Math.min((cast / eligible) * 100, 100) : 0;
  const quorumPct = eligible ? Math.min((quorum / eligible) * 100, 100) : 0;
  return (
    <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
      <div className="h-full bg-primary transition-all duration-500" style={{ width: `${pct}%` }} />
      <div className="absolute top-0 h-full w-[2px] bg-foreground/70"
           style={{ left: `${quorumPct}%` }} />
    </div>
  );
};

export default function Meetings() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [board, setBoard] = useState(null);
  const [meetings, setMeetings] = useState(null);
  const [agenda, setAgenda] = useState(null);
  const [rules, setRules] = useState(null);
  const [ruleForm, setRuleForm] = useState(null);
  const [newMeeting, setNewMeeting] = useState(null);
  const [newItem, setNewItem] = useState(null);
  const [vote, setVote] = useState(null);
  const [step, setStep] = useState(null);
  const [record, setRecord] = useState(null);
  const [consent, setConsent] = useState(null);
  const [busy, setBusy] = useState("");
  const [tab, setTab] = useState("meetings");

  const load = useCallback(async () => {
    const [c, d, m, a, r] = await Promise.all([
      api.get("/governance/config"), api.get("/governance/dashboard"),
      api.get("/governance/meetings"), api.get("/governance/agenda"),
      api.get("/governance/rules"),
    ]);
    setCfg(c.data);
    setBoard(d.data);
    setMeetings(m.data);
    setAgenda(a.data);
    setRules(r.data);
    setRuleForm({ ...c.data.values, note: "" });
  }, []);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const isSecretary = cfg?.you?.is_secretary;
  const isChair = cfg?.you?.is_chair;

  const run = async (key, fn, message) => {
    setBusy(key);
    try {
      await fn();
      toast.success(message);
      await load();
      setNewMeeting(null); setNewItem(null); setVote(null); setStep(null);
      setRecord(null); setConsent(null);
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const openPack = async (ref) => {
    try {
      const res = await api.get(`/governance/meetings/${ref}/pack.pdf`,
        { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) {
      const detail = e?.response?.data instanceof Blob
        ? JSON.parse(await e.response.data.text())?.detail : apiError(e);
      toast.error(detail || apiError(e));
    }
  };

  const openMeetings = useMemo(
    () => (meetings?.items || []).filter((m) => !["minuted", "archived"].includes(m.status)),
    [meetings],
  );

  if (!cfg || !board || !meetings || !agenda) return <Loading label="Opening the board room" />;

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Governance · Phase 1"
                  title="Meetings, agenda and resolutions"
                  description={`Only the Board Secretary puts an item on the agenda, and the plain-or-vote choice is made when it is created. Every resolution is decided under governance rules v${cfg.version}.`}
                  actions={isSecretary && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" data-testid="new-meeting-button"
                              className="rounded-full"
                              onClick={() => setNewMeeting({
                                title: "", meeting_type: "board", starts_at: "", place: "",
                                video_link: "", convener: "", quorum: cfg.values.quorum,
                                notes: "" })}>
                        <CalendarClock className="mr-1.5 h-3.5 w-3.5" /> Call a meeting
                      </Button>
                      <Button size="sm" data-testid="new-item-button"
                              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                              disabled={openMeetings.length === 0}
                              onClick={() => setNewItem({
                                meeting_ref: openMeetings[0]?.meeting_ref || "", kind: "plain",
                                title: "", description: "", category: "other",
                                policy_reference: "", rule_code: "", opens_at: "", closes_at: "",
                                quorum: cfg.values.quorum, majority: cfg.values.majority,
                                supermajority_percent: cfg.values.supermajority_percent,
                                tie_rule: cfg.values.tie_rule,
                                abstentions_count_toward_quorum:
                                  cfg.values.abstentions_count_toward_quorum,
                                anonymous: cfg.values.anonymous_default,
                                electorate_roles: cfg.values.electorate_roles })}>
                        <Plus className="mr-1.5 h-3.5 w-3.5" /> Add an agenda item
                      </Button>
                    </div>
                  )} />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testId="gov-kpi-meetings" label="Meetings in the register" icon={CalendarClock}
             value={board.counters.meetings}
             sub={`${board.upcoming_meetings.length} still to come`} />
        <Kpi testId="gov-kpi-open" label="Resolutions open" accent="amber" icon={Vote}
             value={board.counters.open}
             sub={`${board.awaiting_your_vote.length} waiting on you`} />
        <Kpi testId="gov-kpi-carried" label="Resolutions carried" accent="emerald"
             icon={CheckCircle2} value={board.counters.carried}
             sub={`${board.counters.resolutions} decided in all`} />
        <Kpi testId="gov-kpi-rules" label="Governance rules" accent="violet" icon={ShieldCheck}
             value={`v${board.rules_version}`}
             sub={`quorum ${board.quorum} · ${words(cfg.values.majority)}`} />
      </div>

      {board.awaiting_your_vote.length > 0 && (
        <Panel title="Waiting on your vote" testId="panel-your-votes"
               kicker="You are on the roll for these resolutions">
          <div className="space-y-3">
            {board.awaiting_your_vote.map((i) => (
              <div key={i.item_ref}
                   className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3"
                   data-testid={`your-vote-${i.item_ref}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="text-[13px] font-semibold text-foreground">{i.title}</div>
                    <div className="mt-0.5 text-[11.5px] text-muted-foreground">
                      {i.meeting_title} · closes {when(i.voting.closes_at)} · quorum{" "}
                      {i.voting.quorum} of {i.voting.eligible.length}
                      {i.anonymous && " · ballot anonymous to viewers"}
                    </div>
                  </div>
                  <Button size="sm" data-testid={`vote-open-${i.item_ref}`}
                          className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                          onClick={() => setVote({ ...i, decision: "for", minute: "" })}>
                    <Vote className="mr-1.5 h-3.5 w-3.5" /> Cast your vote
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {isChair && board.awaiting_consent.length > 0 && (
        <Panel title="Short notice needs your consent" testId="panel-consent"
               kicker="The rules ask for more notice than these were given">
          <div className="space-y-2">
            {board.awaiting_consent.map((i) => (
              <div key={i.item_ref}
                   className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-red-500/30 bg-red-500/5 p-3"
                   data-testid={`consent-${i.item_ref}`}>
                <div>
                  <div className="text-[13px] font-semibold text-foreground">{i.title}</div>
                  <div className="text-[11.5px] text-muted-foreground">
                    {i.short_notice.days_given} days' notice where{" "}
                    {i.short_notice.required_days} are required
                  </div>
                </div>
                <Button size="sm" variant="outline" className="rounded-full"
                        data-testid={`consent-open-${i.item_ref}`}
                        onClick={() => setConsent({ ...i, reason: "" })}>
                  <PenLine className="mr-1.5 h-3.5 w-3.5" /> Consent on the record
                </Button>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="gov-tabs">
          <TabsTrigger value="meetings" data-testid="tab-meetings">Meetings</TabsTrigger>
          <TabsTrigger value="agenda" data-testid="tab-agenda">Agenda &amp; resolutions</TabsTrigger>
          <TabsTrigger value="register" data-testid="tab-register">Resolution register</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-gov-rules">Rules</TabsTrigger>
          <TabsTrigger value="annual" data-testid="tab-annual-report">Annual report</TabsTrigger>
        </TabsList>

        <TabsContent value="annual" className="mt-4">
          <AnnualReport meetings={meetings.items} />
        </TabsContent>

        <TabsContent value="meetings" className="mt-4">
          <Panel title="Meetings register" testId="panel-meetings"
                 kicker="Draft → notice issued → held → minuted → archived">
            <Table testId="meetings-table" head={<><Th>Reference</Th><Th>Meeting</Th><Th>When</Th>
              <Th>Where</Th><Th>Quorum</Th><Th>Agenda</Th><Th>Status</Th><Th /></>}>
              {meetings.items.length === 0 && <Empty label="No meetings called yet" colSpan={8} />}
              {meetings.items.map((m) => (
                <tr key={m.meeting_ref} className="border-b border-border/60"
                    data-testid={`meeting-row-${m.meeting_ref}`}>
                  <Td className="font-mono text-[11px]">{m.meeting_ref}</Td>
                  <Td>
                    <div className="font-medium text-foreground">{m.title}</div>
                    <div className="text-[11px] text-muted-foreground">{m.type_label}</div>
                  </Td>
                  <Td className="font-mono text-[11px]">{when(m.starts_at)}</Td>
                  <Td className="max-w-[180px] truncate">{m.place || m.video_link}</Td>
                  <Td className="font-mono text-[11px]">
                    {m.attendees.length}/{m.quorum}
                    {m.quorum_met === false && (
                      <span className="ml-1 text-red-600 dark:text-red-400">not met</span>
                    )}
                  </Td>
                  <Td className="font-mono text-[11px]">{m.agenda_items}</Td>
                  <Td><Chip className={STATUS_STYLE[m.status]}>{words(m.status)}</Chip></Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <button type="button" data-testid={`pack-${m.meeting_ref}`}
                              onClick={() => openPack(m.meeting_ref)}
                              className="inline-flex h-8 items-center rounded-full border border-border px-3 text-[11.5px] text-foreground transition-colors duration-150 hover:border-primary/40">
                        <FileText className="mr-1.5 h-3.5 w-3.5" /> Pack
                      </button>
                      {isSecretary && NEXT_STEP[m.status] && (
                        <Button size="sm" variant="outline" className="h-8 rounded-full"
                                data-testid={`meeting-step-${m.meeting_ref}`}
                                onClick={() => setStep({ meeting: m, status: NEXT_STEP[m.status],
                                  attendee_emails: m.invitees.map((p) => p.email).filter(Boolean),
                                  apology_emails: [], minutes: "" })}>
                          {STEP_LABEL[NEXT_STEP[m.status]]}
                        </Button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="agenda" className="mt-4">
          <div className="space-y-3">
            {agenda.items.length === 0 && (
              <Panel title="Agenda" testId="panel-agenda-empty">
                <p className="text-[12.5px] text-muted-foreground">
                  Nothing on the agenda yet. The Board Secretary adds items to a meeting.
                </p>
              </Panel>
            )}
            {agenda.items.map((i) => (
              <Panel key={i.item_ref} testId={`agenda-${i.item_ref}`}
                     title={`${i.order}. ${i.title}`}
                     kicker={`${i.item_ref} · ${i.meeting_title} · ${i.kind === "voting"
                       ? "resolution" : "discussion"} · ${i.category}`}
                     actions={(
                       <div className="flex flex-wrap items-center gap-2">
                         <Chip className={STATUS_STYLE[i.status]}
                               testId={`agenda-status-${i.item_ref}`}>{words(i.status)}</Chip>
                         {i.status === "draft" && isSecretary && (
                           <Button size="sm" className="h-8 rounded-full"
                                   data-testid={`publish-${i.item_ref}`}
                                   onClick={() => run(`publish-${i.item_ref}`,
                                     () => api.post(`/governance/agenda/${i.item_ref}/publish`),
                                     "Published to the board")}>
                             Publish
                           </Button>
                         )}
                         {i.status === "open_for_voting" && i.you_are_eligible
                           && !i.you_have_voted && (
                           <Button size="sm" className="h-8 rounded-full"
                                   data-testid={`agenda-vote-${i.item_ref}`}
                                   onClick={() => setVote({ ...i, decision: "for", minute: "" })}>
                             Vote
                           </Button>
                         )}
                         {i.status === "open_for_voting" && isSecretary && (
                           <Button size="sm" variant="outline" className="h-8 rounded-full"
                                   data-testid={`close-${i.item_ref}`}
                                   onClick={() => run(`close-${i.item_ref}`,
                                     () => api.post(`/governance/agenda/${i.item_ref}/close`),
                                     "Voting closed and the outcome worked out")}>
                             Close the vote
                           </Button>
                         )}
                         {i.kind === "plain" && !["archived"].includes(i.status) && (
                           <Button size="sm" variant="outline" className="h-8 rounded-full"
                                   data-testid={`record-${i.item_ref}`}
                                   onClick={() => setRecord({ item: i, note: "", decision: "",
                                     action: "", action_owner: "", action_due_on: "" })}>
                             Minute it
                           </Button>
                         )}
                       </div>
                     )}>
                {i.description && (
                  <p className="mb-3 text-[12.5px] leading-relaxed text-muted-foreground">
                    {i.description}
                  </p>
                )}

                {i.kind === "voting" && i.voting && (
                  <div className="space-y-2.5" data-testid={`voting-${i.item_ref}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 text-[11.5px] text-muted-foreground">
                      <span>
                        {i.tally.cast} of {i.voting.eligible.length} voted · turnout{" "}
                        {i.turnout_percent}% · quorum {i.voting.quorum} ·{" "}
                        {words(i.voting.majority)}
                        {i.voting.majority === "supermajority"
                          && ` ${i.voting.supermajority_percent}%`}
                      </span>
                      <span>
                        {i.anonymous ? "anonymous ballot" : "open ballot"} · rules v
                        {i.rules_version}{i.rules_stale && " (as it stood)"}
                      </span>
                    </div>
                    <Bar cast={i.tally.cast} eligible={i.voting.eligible.length}
                         quorum={i.voting.quorum} />
                    <div className="flex flex-wrap gap-2">
                      <Chip className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                            testId={`tally-for-${i.item_ref}`}>{i.tally.for} for</Chip>
                      <Chip className="border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300"
                            testId={`tally-against-${i.item_ref}`}>{i.tally.against} against</Chip>
                      <Chip className="border-border bg-muted text-muted-foreground"
                            testId={`tally-abstain-${i.item_ref}`}>{i.tally.abstain} abstained</Chip>
                      {i.outcome && (
                        <Chip className={OUTCOME_STYLE[i.outcome.status]}
                              testId={`outcome-${i.item_ref}`}>
                          {words(i.outcome.status)}
                        </Chip>
                      )}
                      {i.resolution_ref && (
                        <a href={`${BACKEND_URL}/api/governance/resolutions/${i.item_ref}/certificate.pdf`}
                           target="_blank" rel="noreferrer"
                           data-testid={`certificate-${i.item_ref}`}
                           className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-foreground hover:border-primary/40">
                          <FileText className="mr-1 h-3 w-3" /> {i.resolution_ref}
                        </a>
                      )}
                    </div>
                    {i.short_notice?.short && (
                      <p className="text-[11px] text-amber-600 dark:text-amber-400"
                         data-testid={`short-notice-${i.item_ref}`}>
                        Short notice: {i.short_notice.days_given} days given where{" "}
                        {i.short_notice.required_days} are required
                        {i.short_notice.consent_by
                          ? ` — consented by ${i.short_notice.consent_by}`
                          : " — waiting on the Chair's consent"}
                      </p>
                    )}
                    {i.votes?.length > 0 && (
                      <div className="grid gap-1 pt-1 md:grid-cols-2">
                        {i.votes.map((v, idx) => (
                          <div key={`${i.item_ref}-${idx}`}
                               className="text-[11.5px] text-muted-foreground">
                            <span className="font-medium text-foreground">
                              {v.name || "A member of the board"}
                            </span>{" "}
                            voted {v.decision}{v.minute ? ` — ${v.minute}` : ""}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {i.kind === "plain" && (
                  <div className="space-y-2" data-testid={`plain-${i.item_ref}`}>
                    {(i.discussion || []).map((d, idx) => (
                      <p key={`${i.item_ref}-d${idx}`} className="text-[11.5px] text-muted-foreground">
                        <span className="font-medium text-foreground">{d.by}</span>: {d.note}
                      </p>
                    ))}
                    {i.decision && (
                      <p className="text-[12.5px] text-foreground" data-testid={`decision-${i.item_ref}`}>
                        <span className="font-semibold">Decision:</span> {i.decision}
                      </p>
                    )}
                    {(i.actions || []).map((a, idx) => (
                      <p key={`${i.item_ref}-a${idx}`} className="text-[11.5px] text-muted-foreground">
                        Action: {a.what} — {a.owner}{a.due_on ? ` by ${a.due_on}` : ""}
                      </p>
                    ))}
                  </div>
                )}
              </Panel>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="register" className="mt-4">
          <Panel title="Resolution register" testId="panel-register"
                 kicker="Every decided resolution, with the rules it was decided under">
            <Table testId="register-table" head={<><Th>Resolution</Th><Th>Motion</Th>
              <Th>Meeting</Th><Th>For / against / abstain</Th><Th>Quorum</Th><Th>Outcome</Th>
              <Th /></>}>
              {board.recent_outcomes.length === 0
                && <Empty label="No resolution has been decided yet" colSpan={7} />}
              {board.recent_outcomes.map((i) => (
                <tr key={i.item_ref} className="border-b border-border/60"
                    data-testid={`register-row-${i.item_ref}`}>
                  <Td className="font-mono text-[11px]">{i.resolution_ref}</Td>
                  <Td className="max-w-[240px]">{i.title}</Td>
                  <Td className="text-[11.5px] text-muted-foreground">{i.meeting_title}</Td>
                  <Td className="font-mono text-[11px]">
                    {i.outcome.for} / {i.outcome.against} / {i.outcome.abstain}
                  </Td>
                  <Td className="font-mono text-[11px]">
                    {i.outcome.counted_for_quorum}/{i.outcome.quorum}
                  </Td>
                  <Td><Chip className={OUTCOME_STYLE[i.outcome.status]}>
                    {words(i.outcome.status)}</Chip></Td>
                  <Td className="text-right">
                    <a href={`${BACKEND_URL}/api/governance/resolutions/${i.item_ref}/certificate.pdf`}
                       target="_blank" rel="noreferrer"
                       data-testid={`register-pdf-${i.item_ref}`}
                       className="inline-flex h-8 items-center rounded-full border border-border px-3 text-[11.5px] text-foreground hover:border-primary/40">
                      <FileText className="mr-1.5 h-3.5 w-3.5" /> Certificate
                    </a>
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="rules" className="mt-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title={`Governance rules v${cfg.version}`} testId="panel-gov-rules"
                   kicker="Saving makes a new version — old resolutions keep the version they were decided under"
                   actions={(isSecretary || isChair) && (
                     <Button size="sm" data-testid="rules-save" disabled={busy === "rules"}
                             className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                             onClick={() => run("rules",
                               () => api.put("/governance/rules", ruleForm),
                               "A new version of the governance rules is in force")}>
                       {busy === "rules" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                       Save as a new version
                     </Button>
                   )}>
              <div className="grid gap-3 md:grid-cols-2">
                <F label="Default quorum">
                  <Input type="number" data-testid="rules-quorum" disabled={!isSecretary && !isChair}
                         value={ruleForm?.quorum ?? 3}
                         onChange={(e) => setRuleForm({ ...ruleForm,
                           quorum: Number(e.target.value) })} />
                </F>
                <F label="Default majority">
                  <Select value={ruleForm?.majority} disabled={!isSecretary && !isChair}
                          onValueChange={(v) => setRuleForm({ ...ruleForm, majority: v })}>
                    <SelectTrigger data-testid="rules-majority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {cfg.majorities.map((m) => (
                        <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Supermajority threshold (%)">
                  <Input type="number" step="0.1" data-testid="rules-supermajority"
                         disabled={!isSecretary && !isChair}
                         value={ruleForm?.supermajority_percent ?? 66.7}
                         onChange={(e) => setRuleForm({ ...ruleForm,
                           supermajority_percent: Number(e.target.value) })} />
                </F>
                <F label="Tie handling">
                  <Select value={ruleForm?.tie_rule} disabled={!isSecretary && !isChair}
                          onValueChange={(v) => setRuleForm({ ...ruleForm, tie_rule: v })}>
                    <SelectTrigger data-testid="rules-tie"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {cfg.tie_rules.map((t) => (
                        <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Notice period (days)"
                   hint="Less notice than this needs the President's consent on the record.">
                  <Input type="number" data-testid="rules-notice" disabled={!isSecretary && !isChair}
                         value={ruleForm?.notice_days ?? 14}
                         onChange={(e) => setRuleForm({ ...ruleForm,
                           notice_days: Number(e.target.value) })} />
                </F>
                <F label="Voting window (days)">
                  <Input type="number" data-testid="rules-window" disabled={!isSecretary && !isChair}
                         value={ruleForm?.voting_window_days ?? 7}
                         onChange={(e) => setRuleForm({ ...ruleForm,
                           voting_window_days: Number(e.target.value) })} />
                </F>
              </div>
              <div className="mt-3 space-y-2.5">
                <div className="flex items-center justify-between rounded-xl border border-border p-3">
                  <span className="text-[12px] text-foreground">
                    Abstentions count toward the quorum
                  </span>
                  <Switch data-testid="rules-abstentions"
                          disabled={!isSecretary && !isChair}
                          checked={Boolean(ruleForm?.abstentions_count_toward_quorum)}
                          onCheckedChange={(v) => setRuleForm({ ...ruleForm,
                            abstentions_count_toward_quorum: v })} />
                </div>
                <div className="flex items-center justify-between rounded-xl border border-border p-3">
                  <span className="text-[12px] text-foreground">
                    New resolutions default to an anonymous ballot
                  </span>
                  <Switch data-testid="rules-anonymous" disabled={!isSecretary && !isChair}
                          checked={Boolean(ruleForm?.anonymous_default)}
                          onCheckedChange={(v) => setRuleForm({ ...ruleForm,
                            anonymous_default: v })} />
                </div>
                <F label="Why the change">
                  <Textarea rows={2} data-testid="rules-note" disabled={!isSecretary && !isChair}
                            value={ruleForm?.note || ""}
                            onChange={(e) => setRuleForm({ ...ruleForm, note: e.target.value })} />
                </F>
              </div>
            </Panel>

            <div className="space-y-4">
              <Panel title="Who may vote" testId="panel-electorate"
                     kicker={`${cfg.electorate_preview.length} people hold a vote today`}>
                <div className="space-y-1.5" data-testid="electorate-list">
                  {cfg.electorate_preview.map((p) => (
                    <div key={p.email}
                         className="flex items-center justify-between border-b border-border/60 py-1.5">
                      <span className="text-[12.5px] text-foreground">{p.name}</span>
                      <span className="text-[11px] text-muted-foreground">{words(p.office)}</span>
                    </div>
                  ))}
                </div>
              </Panel>
              <Panel title="Version history" testId="panel-rule-versions"
                     kicker="Nothing is ever overwritten">
                <Table testId="rule-version-table" head={<><Th>Version</Th><Th>Quorum</Th>
                  <Th>Majority</Th><Th>Notice</Th><Th>By</Th></>}>
                  {(rules?.items || []).map((v) => (
                    <tr key={v.version} className="border-b border-border/60"
                        data-testid={`rule-version-${v.version}`}>
                      <Td className="font-mono text-[11px]">
                        v{v.version}{v.version === rules.current && " · in force"}
                      </Td>
                      <Td className="font-mono text-[11px]">{v.values.quorum}</Td>
                      <Td>{words(v.values.majority)}</Td>
                      <Td className="font-mono text-[11px]">{v.values.notice_days}d</Td>
                      <Td className="text-[11px] text-muted-foreground">{v.created_by}</Td>
                    </tr>
                  ))}
                </Table>
              </Panel>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* call a meeting */}
      <Dialog open={Boolean(newMeeting)} onOpenChange={(o) => !o && setNewMeeting(null)}>
        <DialogContent className="max-w-2xl" data-testid="meeting-dialog">
          <DialogHeader><DialogTitle>Call a meeting</DialogTitle>
            <DialogDescription>
              Everyone who holds a vote is invited; you record who actually attended afterwards.
            </DialogDescription></DialogHeader>
          <div className="grid gap-3 md:grid-cols-2">
            <F label="Title">
              <Input data-testid="meeting-title" value={newMeeting?.title || ""}
                     onChange={(e) => setNewMeeting({ ...newMeeting, title: e.target.value })} />
            </F>
            <F label="Kind of meeting">
              <Select value={newMeeting?.meeting_type}
                      onValueChange={(v) => setNewMeeting({ ...newMeeting, meeting_type: v })}>
                <SelectTrigger data-testid="meeting-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {cfg.meeting_types.map((t) => (
                    <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
            <F label="Starts">
              <Input type="datetime-local" data-testid="meeting-starts"
                     value={newMeeting?.starts_at || ""}
                     onChange={(e) => setNewMeeting({ ...newMeeting,
                       starts_at: e.target.value })} />
            </F>
            <F label="Quorum">
              <Input type="number" data-testid="meeting-quorum" value={newMeeting?.quorum ?? 3}
                     onChange={(e) => setNewMeeting({ ...newMeeting,
                       quorum: Number(e.target.value) })} />
            </F>
            <F label="Place">
              <Input data-testid="meeting-place" value={newMeeting?.place || ""}
                     onChange={(e) => setNewMeeting({ ...newMeeting, place: e.target.value })} />
            </F>
            <F label="Video link">
              <Input data-testid="meeting-video" value={newMeeting?.video_link || ""}
                     onChange={(e) => setNewMeeting({ ...newMeeting,
                       video_link: e.target.value })} />
            </F>
          </div>
          <DialogFooter>
            <Button data-testid="meeting-save" disabled={busy === "meeting"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("meeting", () => api.post("/governance/meetings", {
                      ...newMeeting,
                      starts_at: newMeeting.starts_at ? `${newMeeting.starts_at}:00Z` : "",
                    }), "Meeting called")}>
              {busy === "meeting" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Call it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* agenda item */}
      <Dialog open={Boolean(newItem)} onOpenChange={(o) => !o && setNewItem(null)}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto" data-testid="item-dialog">
          <DialogHeader><DialogTitle>Add an agenda item</DialogTitle>
            <DialogDescription>
              Choose now whether the board simply discusses this or votes on it — the choice cannot
              be changed once it is published.
            </DialogDescription></DialogHeader>
          <div className="grid gap-3 md:grid-cols-2">
            <F label="Meeting">
              <Select value={newItem?.meeting_ref}
                      onValueChange={(v) => setNewItem({ ...newItem, meeting_ref: v })}>
                <SelectTrigger data-testid="item-meeting"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {openMeetings.map((m) => (
                    <SelectItem key={m.meeting_ref} value={m.meeting_ref}>
                      {m.meeting_ref} · {m.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
            <F label="Kind">
              <Select value={newItem?.kind}
                      onValueChange={(v) => setNewItem({ ...newItem, kind: v })}>
                <SelectTrigger data-testid="item-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {cfg.item_kinds.map((k) => (
                    <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
            <div className="md:col-span-2">
              <F label="Title">
                <Input data-testid="item-title" value={newItem?.title || ""}
                       onChange={(e) => setNewItem({ ...newItem, title: e.target.value })} />
              </F>
            </div>
            <div className="md:col-span-2">
              <F label="Description">
                <Textarea rows={3} data-testid="item-description" value={newItem?.description || ""}
                          onChange={(e) => setNewItem({ ...newItem,
                            description: e.target.value })} />
              </F>
            </div>
            <F label="Category">
              <Select value={newItem?.category}
                      onValueChange={(v) => setNewItem({ ...newItem, category: v })}>
                <SelectTrigger data-testid="item-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {cfg.categories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
            <F label="Policy or constitution reference">
              <Input data-testid="item-policy" value={newItem?.policy_reference || ""}
                     onChange={(e) => setNewItem({ ...newItem,
                       policy_reference: e.target.value })} />
            </F>
            {newItem?.kind === "voting" && (
              <>
                <F label="Voting opens">
                  <Input type="datetime-local" data-testid="item-opens"
                         value={newItem?.opens_at || ""}
                         onChange={(e) => setNewItem({ ...newItem, opens_at: e.target.value })} />
                </F>
                <F label="Voting closes">
                  <Input type="datetime-local" data-testid="item-closes"
                         value={newItem?.closes_at || ""}
                         onChange={(e) => setNewItem({ ...newItem, closes_at: e.target.value })} />
                </F>
                <F label="Quorum">
                  <Input type="number" data-testid="item-quorum" value={newItem?.quorum ?? 3}
                         onChange={(e) => setNewItem({ ...newItem,
                           quorum: Number(e.target.value) })} />
                </F>
                <F label="Majority rule">
                  <Select value={newItem?.majority}
                          onValueChange={(v) => setNewItem({ ...newItem, majority: v })}>
                    <SelectTrigger data-testid="item-majority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {cfg.majorities.map((m) => (
                        <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Tie handling">
                  <Select value={newItem?.tie_rule}
                          onValueChange={(v) => setNewItem({ ...newItem, tie_rule: v })}>
                    <SelectTrigger data-testid="item-tie"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {cfg.tie_rules.map((t) => (
                        <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <div className="flex items-center justify-between rounded-xl border border-border p-3">
                  <span className="text-[12px] text-foreground">Anonymous ballot</span>
                  <Switch data-testid="item-anonymous" checked={Boolean(newItem?.anonymous)}
                          onCheckedChange={(v) => setNewItem({ ...newItem, anonymous: v })} />
                </div>
                <div className="flex items-center justify-between rounded-xl border border-border p-3">
                  <span className="text-[12px] text-foreground">
                    Abstentions count toward quorum
                  </span>
                  <Switch data-testid="item-abstentions"
                          checked={Boolean(newItem?.abstentions_count_toward_quorum)}
                          onCheckedChange={(v) => setNewItem({ ...newItem,
                            abstentions_count_toward_quorum: v })} />
                </div>
                <div className="md:col-span-2">
                  <F label="Who may vote"
                     hint="Leave as it stands to use the roll from the governance rules.">
                    <div className="flex flex-wrap gap-2">
                      {cfg.electorate_roles.map((r) => {
                        const on = (newItem?.electorate_roles || []).includes(r);
                        return (
                          <button key={r} type="button" data-testid={`item-role-${r}`}
                                  onClick={() => setNewItem({ ...newItem,
                                    electorate_roles: on
                                      ? newItem.electorate_roles.filter((x) => x !== r)
                                      : [...(newItem.electorate_roles || []), r] })}
                                  className={`rounded-full border px-3 py-1 text-[11.5px] transition-colors duration-150 ${
                                    on ? "border-primary/50 bg-primary/10 text-primary"
                                      : "border-border text-muted-foreground hover:border-primary/30"}`}>
                            {words(r)}
                          </button>
                        );
                      })}
                    </div>
                  </F>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="item-save" disabled={busy === "item"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("item", () => api.post("/governance/agenda", {
                      ...newItem,
                      opens_at: newItem.opens_at ? `${newItem.opens_at}:00Z` : "",
                      closes_at: newItem.closes_at ? `${newItem.closes_at}:00Z` : "",
                    }), "On the agenda as a draft — publish it when you are ready")}>
              {busy === "item" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* vote */}
      <Dialog open={Boolean(vote)} onOpenChange={(o) => !o && setVote(null)}>
        <DialogContent data-testid="vote-dialog">
          <DialogHeader><DialogTitle>{vote?.title}</DialogTitle>
            <DialogDescription>
              {vote?.anonymous
                ? "Your name is kept out of what the board sees; the audit trail still holds it."
                : "Your vote is recorded openly in the minutes."} You cannot change it once cast.
            </DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              {["for", "against", "abstain"].map((d) => (
                <button key={d} type="button" data-testid={`vote-${d}`}
                        onClick={() => setVote({ ...vote, decision: d })}
                        className={`flex-1 rounded-xl border px-3 py-2.5 text-[12.5px] font-medium capitalize transition-colors duration-150 ${
                          vote?.decision === d
                            ? "border-primary/50 bg-primary/10 text-primary"
                            : "border-border text-muted-foreground hover:border-primary/30"}`}>
                  {d}
                </button>
              ))}
            </div>
            <F label="Minute (optional)">
              <Textarea rows={3} data-testid="vote-minute" value={vote?.minute || ""}
                        onChange={(e) => setVote({ ...vote, minute: e.target.value })} />
            </F>
          </div>
          <DialogFooter>
            <Button data-testid="vote-save" disabled={busy === "vote"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("vote",
                      () => api.post(`/governance/agenda/${vote.item_ref}/vote`,
                        { decision: vote.decision, minute: vote.minute }),
                      "Your vote is recorded")}>
              {busy === "vote" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Cast it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* meeting step */}
      <Dialog open={Boolean(step)} onOpenChange={(o) => !o && setStep(null)}>
        <DialogContent className="max-w-xl" data-testid="step-dialog">
          <DialogHeader><DialogTitle>{STEP_LABEL[step?.status]}</DialogTitle>
            <DialogDescription>{step?.meeting?.title}</DialogDescription></DialogHeader>
          {step?.status === "held" && (
            <div className="space-y-2">
              <p className="text-[11.5px] text-muted-foreground">
                Tap anyone who did not attend to move them to apologies.
              </p>
              <div className="space-y-1.5" data-testid="attendee-picker-list">
                {(step.meeting.invitees || []).filter((p) => p.email).map((p) => {
                  const here = step.attendee_emails.includes(p.email);
                  return (
                    <button key={p.email} type="button" data-testid={`attendee-${p.email}`}
                            onClick={() => setStep({ ...step,
                              attendee_emails: here
                                ? step.attendee_emails.filter((e) => e !== p.email)
                                : [...step.attendee_emails, p.email],
                              apology_emails: here
                                ? [...step.apology_emails, p.email]
                                : step.apology_emails.filter((e) => e !== p.email) })}
                            className={`flex w-full items-center justify-between rounded-xl border px-3 py-2 text-[12px] transition-colors duration-150 ${
                              here ? "border-emerald-500/40 bg-emerald-500/5 text-foreground"
                                : "border-border text-muted-foreground"}`}>
                      <span>{p.name}</span>
                      <span className="text-[10.5px] uppercase tracking-wider">
                        {here ? "attended" : "apologies"}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11.5px] text-muted-foreground">
                {step.attendee_emails.length} of {step.meeting.quorum} needed for a quorum
              </p>
            </div>
          )}
          {step?.status === "minuted" && (
            <F label="Minutes">
              <Textarea rows={6} data-testid="step-minutes" value={step.minutes}
                        onChange={(e) => setStep({ ...step, minutes: e.target.value })} />
            </F>
          )}
          {["notice_issued", "archived"].includes(step?.status) && (
            <p className="text-[12.5px] text-muted-foreground">
              {step?.status === "notice_issued"
                ? "Everyone invited will be told the date, the place and the agenda."
                : "The meeting and its agenda become a closed historical record."}
            </p>
          )}
          <DialogFooter>
            <Button data-testid="step-save" disabled={busy === "step"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("step",
                      () => api.post(`/governance/meetings/${step.meeting.meeting_ref}/step`, {
                        status: step.status, attendee_emails: step.attendee_emails,
                        apology_emails: step.apology_emails, minutes: step.minutes }),
                      "Meeting register updated")}>
              {busy === "step" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* minute a plain item */}
      <Dialog open={Boolean(record)} onOpenChange={(o) => !o && setRecord(null)}>
        <DialogContent className="max-w-xl" data-testid="record-dialog">
          <DialogHeader><DialogTitle>Minute it</DialogTitle>
            <DialogDescription>
              {record?.item?.title} — discussion, the decision reached and any action it leaves.
            </DialogDescription></DialogHeader>
          <div className="space-y-3">
            <F label="Discussion note">
              <Textarea rows={3} data-testid="record-note" value={record?.note || ""}
                        onChange={(e) => setRecord({ ...record, note: e.target.value })} />
            </F>
            <F label="Decision reached" hint="Only the Secretary records the decision.">
              <Textarea rows={2} data-testid="record-decision" disabled={!isSecretary}
                        value={record?.decision || ""}
                        onChange={(e) => setRecord({ ...record, decision: e.target.value })} />
            </F>
            <div className="grid gap-3 md:grid-cols-3">
              <F label="Action">
                <Input data-testid="record-action" value={record?.action || ""}
                       onChange={(e) => setRecord({ ...record, action: e.target.value })} />
              </F>
              <F label="Owner">
                <Input data-testid="record-owner" value={record?.action_owner || ""}
                       onChange={(e) => setRecord({ ...record, action_owner: e.target.value })} />
              </F>
              <F label="Due">
                <Input type="date" data-testid="record-due" value={record?.action_due_on || ""}
                       onChange={(e) => setRecord({ ...record, action_due_on: e.target.value })} />
              </F>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="record-save" disabled={busy === "record"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("record",
                      () => api.post(`/governance/agenda/${record.item.item_ref}/record`, {
                        note: record.note, decision: record.decision, action: record.action,
                        action_owner: record.action_owner,
                        action_due_on: record.action_due_on }), "Minuted")}>
              {busy === "record" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Record it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* chair consent */}
      <Dialog open={Boolean(consent)} onOpenChange={(o) => !o && setConsent(null)}>
        <DialogContent data-testid="consent-dialog">
          <DialogHeader><DialogTitle>Consent to short notice</DialogTitle>
            <DialogDescription>
              {consent?.title} — {consent?.short_notice?.days_given} days' notice where{" "}
              {consent?.short_notice?.required_days} are required. Your reason goes on the
              resolution and into the minutes.
            </DialogDescription></DialogHeader>
          <F label="Why the board may proceed">
            <Textarea rows={3} data-testid="consent-reason" value={consent?.reason || ""}
                      onChange={(e) => setConsent({ ...consent, reason: e.target.value })} />
          </F>
          <DialogFooter>
            <Button data-testid="consent-save" disabled={busy === "consent"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("consent",
                      () => api.post(`/governance/agenda/${consent.item_ref}/consent`,
                        { reason: consent.reason }),
                      "Consented — the resolution is open for voting")}>
              {busy === "consent" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Consent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <p className="text-[11px] text-muted-foreground" data-testid="gov-footnote">
        Signed in as {user?.name} · {(cfg.you.is_secretary && "Board Secretary")
        || (cfg.you.is_chair && "President / Chairperson")
        || (cfg.electorate_preview.some((p) => p.email === cfg.you.email) && "board member")
        || "read-only observer"}
        <Gavel className="ml-1.5 inline h-3 w-3" />
      </p>
    </div>
  );
}
